import { SearchResultType, useUniversalSearchQuery } from "@qw/graphql";
import { useMemo } from "react";
import { useDebounceValue } from "usehooks-ts";

import { type BookInsured, useBookInsureds } from "./use-book-insureds";
import { useFuzzyInsuredSearch } from "./use-filtered-insureds";

// `search(term)` is a raw-SQL ILIKE + FTS scan, so it gets a settled term rather than one query per
// keystroke. The fuzzy pass stays on the live term because it is purely local.
const SERVER_SEARCH_DEBOUNCE_MS = 200;
const MINIMUM_SERVER_TERM_LENGTH = 3;

const getInsuredName = (insured: BookInsured) => insured.name;

/**
 * The book only holds what its queries fetched — Won is bounded by the year slider, Lost loads only
 * inactive opportunities, marketing plans depend on `showExpired` — so fuzzy-matching it alone
 * silently misses anything outside those windows. `search(term)` reaches the rest.
 *
 * The two stay separate rather than blended: `search(term)` applies no agency restriction to
 * internal users, so for a broker it spans all of QuoteWell. That is fine to show and wrong to pass
 * off as their own book.
 *
 * The term is a parameter because its two callers hold different ones: the search box suggests
 * against the in-progress term, while the book's results view renders the committed `?term=`.
 */
export const useSearchInsureds = (term: string) => {
  const bookInsureds = useBookInsureds();
  const [settledTerm] = useDebounceValue(term, SERVER_SEARCH_DEBOUNCE_MS);

  const book = useFuzzyInsuredSearch(bookInsureds, term, getInsuredName);

  const { data: { search } = {}, loading } = useUniversalSearchQuery({
    variables: { term: settledTerm },
    skip: settledTerm.length < MINIMUM_SERVER_TERM_LENGTH,
    fetchPolicy: "cache-first",
  });

  const other = useMemo(() => {
    // Deduped against the rendered book rows, not the whole book: an insured the book loaded but
    // the fuzzy pass missed (a DBA hit, say) is invisible above, so dropping it here hides it.
    const rendered = new Set(book.map((insured) => insured.id));
    const results: BookInsured[] = [];

    for (const result of search ?? []) {
      if (result.type !== SearchResultType.Insured || rendered.has(result.insuredId)) {
        continue;
      }

      rendered.add(result.insuredId);
      results.push({ id: result.insuredId, name: result.insuredName });
    }

    return results;
  }, [book, search]);

  return { book, other, loading };
};
